import { useEffect, useRef, useState } from "react";
import { trackEngagement } from "../../lib/engagement";

/**
 * Audio playback for a radio screen, shared by every scheme.
 *
 * `toggle(id)` starts streaming that station's `streamUrl`, or stops it if it's
 * already the one playing. Switching ids switches the stream; unmounting (the
 * guest leaving the Radio screen) stops it. `play()` rejections - autoplay
 * blocked, bad codec (HLS), mixed-content http:// - are swallowed so the UI
 * never throws; playback needs a direct HTTPS MP3/AAC `streamUrl`.
 *
 * Render the returned ref as `<audio ref={audioRef} preload="none" />`.
 */
export function useRadioPlayback(
  stations: Array<{ id: string; name?: string; streamUrl?: string }>
) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggle = (id: string | null | undefined) => {
    if (!id) return;
    // Count a START only - toggling the same station off isn't a new play, and
    // re-counting it would double every listen. Tracked here rather than inside
    // the state updater, which React may invoke twice in development.
    if (playingId !== id) {
      trackEngagement(
        "radio_play",
        id,
        stations.find((s) => s.id === id)?.name
      );
    }
    setPlayingId((cur) => (cur === id ? null : id));
  };

  const playingUrl = playingId
    ? stations.find((s) => s.id === playingId)?.streamUrl
    : undefined;

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (playingUrl) {
      if (el.src !== playingUrl) el.src = playingUrl;
      void el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [playingUrl]);

  useEffect(
    () => () => {
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.removeAttribute("src");
      }
    },
    []
  );

  return { playingId, toggle, audioRef };
}
